import React, { useEffect, useRef, useState } from 'react';
import { Input, Select } from 'antd';
import { getCountries, getCountryCallingCode, parsePhoneNumber, type CountryCode } from 'libphonenumber-js';
import { parsePhone, toFlagEmoji } from '@/utils/parsePhone';
import { usePartnerPhoneCountry } from '@/utils/hooks';

const PRIORITY_COUNTRIES: CountryCode[] = ['DE', 'AT', 'CH', 'US', 'GB', 'FR', 'NL', 'BE', 'IT', 'ES', 'PL'];

const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });

const makeOption = (cc: CountryCode) => ({
  value: cc,
  label: `${toFlagEmoji(cc)} +${getCountryCallingCode(cc)}`,
  search: `${cc} +${getCountryCallingCode(cc)} ${displayNames.of(cc) ?? ''}`.toLowerCase(),
});

const COUNTRY_OPTIONS = [
  ...PRIORITY_COUNTRIES.map(makeOption),
  { value: '__sep__', label: '──────────────', disabled: true, search: '' },
  ...getCountries()
    .filter((cc) => !PRIORITY_COUNTRIES.includes(cc))
    .sort()
    .map(makeOption),
];

const toE164 = (input: string, countryCode: CountryCode): string | undefined => {
  const raw = input.trim().replace(/^00/, '+');
  try {
    const p = parsePhoneNumber(raw, countryCode);
    if (p?.isValid()) return p.number as string;
  } catch {
    // not parseable, fall back to composing it from the digits
  }
  const digits = raw.replace(/\D/g, '');
  if (!digits) return undefined;
  return raw.startsWith('+') ? `+${digits}` : `+${getCountryCallingCode(countryCode)}${digits.replace(/^0/, '')}`;
};

// Invalid international numbers (saved without validation) still get their flag from the calling code.
const fromCallingCode = (input?: string): { country: CountryCode; national: string } | undefined => {
  const digits = (input || '').trim().replace(/^00/, '+');
  if (!digits.startsWith('+')) return undefined;
  try {
    const p = parsePhoneNumber(digits);
    const country =
      p.country ?? getCountries().find((cc) => getCountryCallingCode(cc) === p.countryCallingCode);
    return country ? { country, national: `0${p.nationalNumber}` } : undefined;
  } catch {
    return undefined;
  }
};

type Props = {
  value?: string;
  onChange?: (e164: string) => void;
};

export default function PhoneNumberInput({ value, onChange }: Props) {
  const defaultCountry = usePartnerPhoneCountry();
  const [country, setCountry] = useState<CountryCode>(defaultCountry);
  const [national, setNational] = useState('');

  const wrapperRef = useRef<HTMLDivElement>(null);
  const countryRef = useRef<CountryCode>(defaultCountry);
  const defaultCountryRef = useRef(defaultCountry);
  const nationalRef = useRef('');
  const valueRef = useRef(value);
  const lastEmittedRef = useRef<string | undefined>(undefined);
  const onChangeRef = useRef(onChange);
  // True while the country Select dropdown is open — prevents the wrapper's
  // onBlur from firing during keyboard-driven dropdown navigation, because
  // Ant Design renders the dropdown in a portal (outside the wrapper DOM).
  const selectOpenRef = useRef(false);

  countryRef.current = country;
  nationalRef.current = national;
  valueRef.current = value;
  defaultCountryRef.current = defaultCountry;
  onChangeRef.current = onChange;

  const resetToValue = () => {
    // Stored numbers are often national ("0176…"), so parse them against the partner's country.
    const parsed = parsePhone(valueRef.current, defaultCountryRef.current);
    if (parsed) {
      setCountry(parsed.country as CountryCode);
      setNational(parsed.national);
      return;
    }
    const fallback = fromCallingCode(valueRef.current);
    setCountry(fallback?.country ?? defaultCountryRef.current);
    setNational(fallback?.national ?? (valueRef.current || ''));
  };

  useEffect(() => {
    lastEmittedRef.current = value ? toE164(value, defaultCountry) : undefined;
    resetToValue();
  }, [value, defaultCountry]);

  // No validity check on purpose: whatever the user picked or typed is saved.
  const tryEmit = (nationalValue: string, countryCode: CountryCode) => {
    const e164 = toE164(nationalValue, countryCode);
    // The backend rejects clearing the phone (400 for both "" and null), so restore the stored number.
    if (!e164) return resetToValue();
    // Stored "0049…" and "+49…" are the same number; also skips the blur that follows a country change.
    if (e164 === lastEmittedRef.current) return;
    lastEmittedRef.current = e164;
    onChangeRef.current?.(e164);
  };

  return (
    <div
      ref={wrapperRef}
      onBlur={(e) => {
        if (selectOpenRef.current) return;
        if (!wrapperRef.current?.contains(e.relatedTarget as Node)) {
          tryEmit(nationalRef.current, countryRef.current);
        }
      }}
    >
      <Input
        addonBefore={
          <Select
            value={country}
            onChange={(cc: string) => {
              const code = cc as CountryCode;
              countryRef.current = code;
              setCountry(code);
              tryEmit(nationalRef.current, code);
            }}
            onOpenChange={(open) => {
              selectOpenRef.current = open;
            }}
            showSearch
            filterOption={(input, option: any) =>
              option?.search?.includes(input.toLowerCase()) ?? false
            }
            options={COUNTRY_OPTIONS}
            popupMatchSelectWidth={false}
            style={{ width: 85 }}
            dropdownStyle={{ minWidth: 200 }}
            labelRender={({ value: v }) => toFlagEmoji(v as string)}
          />
        }
        value={national}
        onChange={(e) => {
          nationalRef.current = e.target.value;
          setNational(e.target.value);
        }}
      />
    </div>
  );
}
